import { _ } from "../../core/polyglot/polyglot.js";
import {
	findBestFormatForNumbers,
	formatCurrency,
} from "../../core/helpers/currency-formatting-util.js";
import BusinessTrendChart from "./BusinessTrendChart.js";
import BusinessTrendInsightWidget from "./BusinessTrendInsightWidget.js";
import HighchartWidget from "../../core/widgets/HighchartWidget.js";
import jQuery from "jquery";
import LegendWidget from "../../core/widgets/LegendWidget.js";
import nullPeriod from "../../models/period/nullPeriod.js";
import responsive from "../../core/responsive.js";

/**
 * Widget letting the user compare the business results of the current
 * year to another. The widget displays a chart (defined in
 * BusinessTrendChart) and a legend.
 */
export default class BusinessTrendWidget extends HighchartWidget {
	constructor() {
		super(...arguments);

		this._chartId = "business-chart-container";

		this._formatter = formatCurrency;

		this._legend = new LegendWidget({
			chart: this,
			legendId: "business-chart-legend",
			legendItemClass: "col-lg-3 col-sm-6 col-xs-6 col-xxs-12",
			itemOptions: [
				{
					titleRenderer: (value) => (html) => {
						if (value < 0) {
							html.render(_("Loss after financial items"));
						} else {
							html.render(_("Profit after financial items"));
						}
					},
					seriesId: "profit",
					pairedTo: "profit_compared",
					onClick: this._onLegendClicked.bind(this),
					valueRenderer:
						this._legendItemValueRenderer("profit_compared"),
				},
				{
					titleRenderer: (value) => (html) => {
						if (value < 0) {
							html.render(
								_("Accumulated loss after financial items"),
							);
						} else {
							html.render(
								_("Accumulated profit after financial items"),
							);
						}
					},
					seriesId: "accumulated-profit",
					pairedTo: "accumulated-profit_compared",
					onClick: (event, show) => {
						// Hack since highcharts seems to broken
						// The green square remains on screen
						const labels = jQuery(".highcharts-data-labels");
						if (show) {
							jQuery(labels[2]).show();
						} else {
							jQuery(labels[2]).hide();
						}
						this._onLegendClicked();
					},
					valueRenderer: this._legendItemValueRenderer(
						"accumulated-profit_compared",
					),
				},
				{
					title: _("Income"),
					seriesId: "income",
					pairedTo: "income_compared",
					active: false,
					onClick: this._onLegendClicked.bind(this),
					valueRenderer:
						this._legendItemValueRenderer("income_compared"),
				},
				{
					title: _("Accumulated income"),
					seriesId: "accumulated-income",
					pairedTo: "accumulated-income_compared",
					active: false,
					onClick: this._onLegendClicked.bind(this),
					valueRenderer: this._legendItemValueRenderer(
						"accumulated-income_compared",
					),
				},
			],
		});
	}

	setComparisonData(primaryComparisonData, secondaryComparisonData) {
		this._primaryComparisonData = primaryComparisonData;
		this._secondaryComparisonData = secondaryComparisonData;

		this._setup();
		this._chart.setCurrentPeriod(this._getCurrentPeriod());

		this._insightWidgets.forEach((insight) => {
			insight.setComparisonData(
				primaryComparisonData,
				secondaryComparisonData,
			);
		});

		const values = [
			primaryComparisonData.getProfitTrend(),
			primaryComparisonData.getAccumulatedProfitTrend(),
			primaryComparisonData.getIncomeTrend(),
			primaryComparisonData.getAccumulatedIncomeTrend(),
			secondaryComparisonData.getProfitTrend(),
			secondaryComparisonData.getAccumulatedProfitTrend(),
			secondaryComparisonData.getIncomeTrend(),
			secondaryComparisonData.getAccumulatedIncomeTrend(),
		];

		this._formatter = findBestFormatForNumbers(values.flat());

		// Make sure we have one entry for each period
		this._extendValuesToContainEntriesForEveryDataPoint(
			values,
			primaryComparisonData.getPeriodLabels(),
		);

		this.setData(
			values.map((entries) =>
				this._chart.monthValuesTo2DCoordinate(
					entries,
					primaryComparisonData.getPeriod().getYear().asMonths(),
				),
			),
		);

		this._updateLegendValues();
	}

	renderContentOn(html) {
		super.renderContentOn(html);
		this._renderInsightsOn(html);
	}

	_setup() {
		this._chart = new BusinessTrendChart({
			currentPeriod: this._getCurrentPeriod(),
			onPlotbandHovered: (plotband) => {
				this._updateLegendValuesForPeriodIndex(plotband.options.index);
			},
			onPlotbandExited: () => {
				this._updateLegendValues(
					this._getCurrentPeriod(),
					this._comparedPeriod,
				);
			},
			onPlotbandClicked: (plotband) => {
				this._redirectToPeriod(plotband.options.period);
			},
			onXAxisUpdated: (axisId, axisOptions) => {
				if (!this._highchart) {
					return;
				}
				this._highchart.get(axisId).update(axisOptions);
			},
			visibleSeriesIds: this._getVisibleSeriesIds(),
		});

		this._chartOptions = this._chart.getChartOptions();

		this._insightWidgets = this._chartOptions.series.slice(0, 4).map(
			(serie) =>
				new BusinessTrendInsightWidget({
					serie,
				}),
		);
	}

	_getVisibleSeriesIds() {
		if (!this._chartOptions.series) return undefined;

		return this._chartOptions.series
			.filter((series) => series.visible)
			.map((series) => series.id);
	}

	_didAttach() {
		this._setLabelVisibility();
	}

	_legendItemValueRenderer(comparedSeriesId) {
		return (value) => {
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			const formatter = (value) =>
				value === null ? "-" : this._formatter(value);

			const isSeriesVisible = this.isSeriesVisible(comparedSeriesId);

			return (html) => {
				html.div(
					{ klass: "legend-item-comparison" },
					html.div(
						{ klass: "legend-item-current" },
						formatter(value.currentValue),
					),
					html
						.div(
							{
								klass: [
									"legend-item-compared",
									comparedSeriesId,
									{
										"comparison-on": isSeriesVisible,
										"comparison-off": !isSeriesVisible,
									},
								],
							},
							html.div(
								{ klass: "legend-item-name" },
								_("Compared to {period}", {
									period: this._secondaryComparisonData.getName(),
								}),
							),
							html.span(
								{ klass: "legend-item-comparison-value" },
								formatter(value.comparedValue),
							),
						)
						.click((event) => {
							event.preventDefault();
							event.stopPropagation();
							this.toggleSeries(comparedSeriesId);
							this._legend.update();
							this._updateInsights();
						}),
				);
			};
		};
	}

	_extendValuesToContainEntriesForEveryDataPoint(values, points) {
		values.forEach((entries) => {
			while (entries.length < points.length) {
				entries.push(null);
			}
		});
	}

	_updateLegendValues() {
		const currentValues = this._primaryComparisonData.getTrendValues();
		const comparedValues = this._secondaryComparisonData.getTrendValues();

		const values = {};

		Object.keys(currentValues).forEach((key) => {
			values[key] = {
				currentValue: currentValues[key],
				comparedValue: comparedValues[key],
			};
		});

		this._legend.setValues(values);

		Object.keys(values).forEach((key) =>
			this._insightWidgets
				.find((widget) => widget.getSerieId() === key)
				.setValues(values[key]),
		);
	}

	_updateLegendValuesForPeriodIndex(periodIndex) {
		const values = {};

		this._chartOptions.series.slice(0, 4).forEach((series, index) => {
			values[series.id] = {
				currentValue: this._data[index][periodIndex].y,
				comparedValue: this._data[index + 4][periodIndex].y,
			};
		});

		this._legend.setValues(values);
	}

	_updateInsights() {
		this._insightWidgets.forEach((widget) => widget.update());
	}

	_redirectToPeriod(period) {
		this._router.setParameters({
			period: period.toString(),
		});
	}

	_onLegendClicked() {
		this._setLabelVisibility();
		this._updateInsights();
	}

	/**
	 * This function has for goal to hide the plot labels on
	 * mobile, except for the accumulated result.
	 */
	_setLabelVisibility() {
		if (!responsive.isMobileBreakpoint()) {
			return;
		}

		if (!this._highchart) {
			return;
		}

		// The series at index 1 is accumulated result
		const visibleSeriesLabelIndex = 1;

		this._highchart.series.forEach((series, index) => {
			if (!series.visible || index === visibleSeriesLabelIndex) {
				return;
			}

			series.data.forEach((datum) => {
				if (!datum.dataLabel) {
					return;
				}
				datum.dataLabel.hide();
			});
		});
	}

	_renderInsightsOn(html) {
		html.div(
			{ klass: "business-insights", "data-test": "insights" },
			this._insightWidgets,
		);
	}

	_getCurrentPeriod() {
		const comparisonData = this._primaryComparisonData;

		return comparisonData ? comparisonData.getPeriod() : nullPeriod;
	}
}
